import { useHistory, useParams } from 'react-router-dom';
import { DATE, formatDateNonUTC } from '@utils/date';
import { NavCode, RefreshType, TemplateHistory } from '@features/survey-templates/types';
import './styles.scss';
import { useBulkSurveysRefreshMutation, useGetHistoryQuery } from '@api/survey-templates-history';
import { TemplateHistoryOptions } from './TemplateHistoryOptions';
import { BulkSurveysModal } from './bulk-surveys-modal/BulkSurveysModal';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { Loader } from '@g17eco/atoms/loader';
import { JobStatus } from '@g17eco/types/background-jobs';
import { SURVEY } from '@constants/terminology';
import { Table, ColumnDef } from '@g17eco/molecules/table';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { RefreshConfirmationModal } from './RefreshConfirmationModal';
import { useRefreshWithConfirmation } from './useRefreshWithConfirmation';

const getStatusText = (row: TemplateHistory) => {
  if (row.deletedDate) {
    return 'Deleted';
  }
  if (row.completedDate) {
    return 'Completed';
  }
  return '';
}

export const TemplateHistoryTable = () => {
  const { initiativeId, templateId, historyId } = useParams<{
    initiativeId: string;
    templateId: string;
    historyId?: string;
  }>();
  const query = useGetHistoryQuery(templateId, { skip: !templateId });
  const history = useHistory();
  const [refreshSurveys, { isLoading: isRefreshing }] = useBulkSurveysRefreshMutation();

  const handleRefresh = async ({
    historyId: refreshHistoryId,
    refreshType,
  }: {
    historyId: string;
    refreshType: RefreshType;
  }) => {
    let title = '';
    let message = '';

    switch (refreshType) {
      case RefreshType.Scope:
        title = 'Refresh scope';
        message = 'The scope refresh process has started. You will receive a notification once it is completed.';
        break;
      case RefreshType.Workgroup:
        title = 'Refresh delegated workgroups';
        message =
          'The delegated workgroups refresh process has started. You will receive a notification once it is completed.';
        break;
      case RefreshType.Settings:
        title = `Refresh ${SURVEY.SINGULAR} settings`;
        message = `The ${SURVEY.SINGULAR} settings refresh process has started. You will receive a notification once it is completed.`;
        break;
    }

    refreshSurveys({
      templateId,
      historyId: refreshHistoryId,
      refreshTypes: [refreshType],
    })
      .unwrap()
      .then(() => {
        generateToast({
          title: `${title} process started`,
          message,
          color: 'success',
        });
      })
      .catch((e) => {
        toastFromError({ error: e, title: `${title} failed` });
      });
  };

  const { initiateRefresh, confirmRefresh, cancelRefresh, isCheckingDiff, confirmationData } =
    useRefreshWithConfirmation({
      templateId,
      onRefresh: handleRefresh,
    });

  const columns: ColumnDef<TemplateHistory>[] = [
    {
      header: 'Date used',
      accessorFn: (row) => formatDateNonUTC(row.created, DATE.DEFAULT_SPACES_WITH_TIME),
    },
    {
      header: 'Last updated',
      accessorFn: (row) => (row.lastUpdated ? formatDateNonUTC(row.lastUpdated, DATE.DEFAULT_SPACES_WITH_TIME) : ''),
    },
    {
      header: SURVEY.CAPITALIZED_PLURAL,
      accessorFn: (row) => row.results.length,
    },
    {
      header: 'Successes',
      accessorFn: (row) => row.results.filter((result) => result.status === JobStatus.Completed).length,
    },
    {
      header: 'Failures',
      accessorFn: (row) => row.results.filter((result) => result.status === JobStatus.Error).length,
    },
    {
      id: 'settings',
      header: '',
      cell: ({ row }) => (
        <TemplateHistoryOptions
          templateHistory={row.original}
          isRefreshing={isRefreshing}
          isCheckingDiff={isCheckingDiff}
          initiateRefresh={initiateRefresh}
          disabled={Boolean(row.original.deletedDate)}
        />
      ),
    },
    {
      header: 'Status',
      accessorFn: (row: TemplateHistory) => getStatusText(row),
    },
  ];

  const handleClose = () => {
    query.refetch();

    history.push(
      `${generateUrl(ROUTES.SURVEY_TEMPLATES_VIEW, {
        initiativeId,
        templateId,
        page: NavCode.History,
      })}`,
    );
  };

  return (
    <>
      {isRefreshing || isCheckingDiff ? <Loader /> : null}
      <Table
        className='mt-3'
        sortBy={[{ id: 'lastEdit', desc: false }]}
        responsive={true}
        pageSize={10}
        columns={columns}
        data={query.data ?? []}
      />
      <BulkSurveysModal isOpen={!!historyId} templateId={templateId} historyId={historyId} toggle={handleClose} />
      {confirmationData && (
        <RefreshConfirmationModal
          modifiedSurveys={confirmationData.modifiedSurveys}
          refreshType={confirmationData.refreshType}
          isRefreshing={isRefreshing}
          onConfirm={confirmRefresh}
          onCancel={cancelRefresh}
        />
      )}
    </>
  );
};
